/**
 * übernommen aus verlinktem Java-Code von https://ec.europa.eu/taxation_customs/tin/tinRequest.html
 * (https://ec.europa.eu/taxation_customs/tin/specs/TIN%20Algorithms-Public_sources.jar)
 */

export class DeAlgorithm {
  private static LENGTH_1 = 11;
  private static LENGTH_PATTERN_1 = /^\d{11}$/;
  private static PATTERN_1 = /[1-9]\d{10}/;
  private static LENGTH_PATTERN_2 = /^\d{11}$/;
  private static PATTERN_2 = /[1-9]\d{10}/;

  private static resultToString(result: number): string | null {
    switch (result) {
      case 4:
        return 'Die TIN muss aus 11 Ziffern bestehen.'; // 'INVALID_LENGTH';
      case 3:
        return 'Die eingegebene TIN ist ungültig.'; // 'INVALID_PATTERN';
      case 2:
        return 'Die TIN muss aus 11 Ziffern bestehen.'; // 'NO_SYNTAX_CHECKER';
      case 1:
        return 'Die eingegebene TIN ist ungültig.'; // 'INVALID_SYNTAX';
      case 0:
        return null; // 'VALID';
      case -1:
        return 'Die eingegebene TIN ist ungültig.'; // 'NO_INFORMATION';
      default:
        return 'Die eingegebene TIN ist ungültig.'; // 'UNKNOWN';
    }
  }

  public validate(tin: string): string {
    const normalizedTIN = tin ? tin.replace(/\//g, '').replace(/\s/g, '').replace(/\./g, '').replace(/-/g, '') : '';
    if (!this.isFollowLength(normalizedTIN)) {
      return DeAlgorithm.resultToString(4);
    } else if (this.isFollowLength(normalizedTIN) && !this.isFollowPattern(normalizedTIN)) {
      return DeAlgorithm.resultToString(3);
    } else {
      return !this.isFollowRules(normalizedTIN) ? DeAlgorithm.resultToString(1) : DeAlgorithm.resultToString(0);
    }
  }

  public isFollowLength(tin: string): boolean {
    return this.isFollowLength1(tin) || this.isFollowLength2(tin);
  }

  public isFollowLength1(tin: string): boolean {
    return DeAlgorithm.LENGTH_PATTERN_1.test(tin);
  }

  public isFollowLength2(tin: string): boolean {
    return DeAlgorithm.LENGTH_PATTERN_2.test(tin);
  }

  public isFollowPattern(tin: string): boolean {
    const followPattern1 = this.isFollowPattern1(tin);
    const followPattern2 = this.isFollowPattern2(tin);
    return followPattern1 || followPattern2;
  }

  public isFollowPattern1(tin: string): boolean {
    if (!DeAlgorithm.PATTERN_1.test(tin)) {
      return false;
    }

    const tab: number[] = new Array<number>(10).fill(0);

    for (let i = 0; i < 10; ++i) {
      tab[i] = parseInt(tin.charAt(i), 10);
    }

    const pos = new Array<number>(10).fill(0);

    for (let i = 0; i < 10; ++i) {
      ++pos[tab[i]];
    }

    let isEncounteredTwice2 = false;
    let isEncountered0 = false;

    for (let i = 0; i < 10; ++i) {
      if (pos[i] === 2) {
        if (isEncounteredTwice2) {
          return false;
        }

        isEncounteredTwice2 = true;
      }

      if (pos[i] === 0) {
        if (isEncountered0) {
          return false;
        }

        isEncountered0 = true;
      }
    }

    return isEncountered0;
  }

  public isFollowPattern2(tin: string): boolean {
    if (!DeAlgorithm.PATTERN_2.test(tin)) {
      return false;
    }

    const tab = new Array<number>(10).fill(0);

    let i: number;
    for (i = 0; i < 10; ++i) {
      tab[i] = parseInt(tin.charAt(i), 10);
    }

    for (i = 0; i < 8; ++i) {
      if (tab[i] === tab[i + 1] && tab[i + 1] === tab[i + 2]) {
        return false;
      }
    }

    const pos = new Array<number>(10).fill(0);

    for (i = 0; i < 10; ++i) {
      ++pos[tab[i]];
    }

    let isEncounteredTwice2 = false;
    let isEncounteredThrice3 = false;

    for (let j = 0; j < 10; ++j) {
      if (pos[j] > 3) {
        return false;
      }

      if (pos[j] === 3) {
        if (isEncounteredThrice3) {
          return false;
        }

        isEncounteredThrice3 = true;
      }

      if (pos[j] === 2) {
        if (isEncounteredTwice2) {
          return false;
        }

        isEncounteredTwice2 = true;
      }
    }

    return isEncounteredThrice3 || isEncounteredTwice2;
  }

  public isFollowRules(tin: string): boolean {
    return (DeAlgorithm.LENGTH_1 === tin.length && this.isFollowRuleGermany1(tin)) || this.isFollowRuleGermany2(tin);
  }

  public isFollowRuleGermany1(tin: string): boolean {
    const c1 = parseInt(tin.charAt(0), 10);
    const c = new Array<number>(9).fill(0);

    let result;
    for (result = 0; result < 9; ++result) {
      c[result] = parseInt(tin.charAt(result + 1), 10);
    }

    result = (c1 + 10) % 10;
    if (result === 0) {
      result = 10;
    }

    result *= 2;
    let x = result % 11;

    let c11;
    for (c11 = 0; c11 < 9; ++c11) {
      x = (x + c[c11]) % 10;
      if (x === 0) {
        x = 10;
      }

      x *= 2;
      x %= 11;
    }

    c11 = parseInt(tin.charAt(10), 10);
    const total = 11 - x;
    if (total === 10) {
      return c11 === 0;
    } else {
      return total === c11;
    }
  }

  public isFollowRuleGermany2(tin: string): boolean {
    return parseInt(tin.charAt(10), 10) === this.calculateCheckDigit(tin);
  }

  public calculateCheckDigit(idnrString: string): number {
    const chars = idnrString.split('');
    let remainderModEleven = 10;
    const length = idnrString.length;

    let digit: number;
    for (let counter = 0; counter < length - 1; ++counter) {
      digit = parseInt(chars[counter], 10);
      let remainderModTen = (digit + remainderModEleven) % 10;
      if (remainderModTen === 0) {
        remainderModTen = 10;
      }
      remainderModEleven = (2 * remainderModTen) % 11;
    }

    digit = 11 - remainderModEleven;
    if (digit === 10) {
      digit = 0;
    }
    return digit;
  }
}
